import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "./style.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  "Tecnologia",
  "Saúde",
  "Educação",
  "Viagem",
  "Comida",
  "Estilo",
  "Negócios",
  "Lifestyle",
  "DIY",
  "Reviews",
  "Tutorial",
  "Notícias",
  "Outro",
];

export default function BlogCreatePage() {
  const { user } = useSelector((state) => ({ ...state }));
  const navigate = useNavigate();
  const { postId } = useParams();
  const [isEditing] = useState(!!postId);
  const [loading, setLoading] = useState(false);
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    category: "Outro",
    tags: "",
    visibility: "public",
    images: [],
  });

  if (!user) {
    navigate("/login");
    return null;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setCoverImage(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setCoverPreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      alert("Título é obrigatório");
      return;
    }

    if (!formData.content.trim()) {
      alert("Conteúdo é obrigatório");
      return;
    }

    if (formData.content.length < 100) {
      alert("O conteúdo deve ter no mínimo 100 caracteres");
      return;
    }

    try {
      setLoading(true);

      // Upload da imagem de capa se houver
      let coverImageData = null;
      if (coverImage) {
        const imgFormData = new FormData();
        imgFormData.append("file", coverImage);
        imgFormData.append("upload_preset", "socializabd_blog");

        const imgRes = await axios.post(
          "https://api.cloudinary.com/v1_1/dsfn6qy3h/image/upload",
          imgFormData
        );

        coverImageData = {
          url: imgRes.data.secure_url,
          public_id: imgRes.data.public_id,
        };
      }

      const payload = {
        ...formData,
        tags: formData.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag),
        coverImage: coverImageData,
      };

      const config = {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
        },
      };

      let response;
      if (isEditing && postId) {
        response = await axios.put(
          `${API_URL}/api/blog/${postId}`,
          payload,
          config
        );
        alert("Artigo atualizado com sucesso!");
      } else {
        response = await axios.post(
          `${API_URL}/api/blog/create`,
          payload,
          config
        );
        alert("Artigo criado com sucesso!");
      }

      navigate(`/blog`);
    } catch (error) {
      console.error("Erro:", error);
      alert(
        error.response?.data?.error ||
          "Erro ao criar/atualizar artigo. Tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="blog_create_page">
      <div className="blog_create_container">
        <div className="blog_create_header">
          <button onClick={() => navigate(-1)} className="blog_back_btn">
            ← Voltar
          </button>
          <h1>{isEditing ? "Editar Artigo" : "Novo Artigo"}</h1>
        </div>

        <form onSubmit={handleSubmit} className="blog_create_form">
          {/* Cover Image */}
          <div className="blog_form_group">
            <label>Imagem de Capa</label>
            <div className="blog_cover_upload">
              {coverPreview ? (
                <img src={coverPreview} alt="Preview" className="blog_cover_preview" />
              ) : (
                <div className="blog_cover_placeholder">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-5.04-6.71l-2.75 3.54-2.03-2.71-1.41 1.41L10.5 19l4.96-6.29z" />
                  </svg>
                  <p>Adicionar imagem de capa</p>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleCoverImageChange}
                className="blog_file_input"
              />
            </div>
          </div>

          {/* Title */}
          <div className="blog_form_group">
            <label htmlFor="title">Título *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Digite o título do seu artigo"
              maxLength="300"
              className="blog_input"
            />
            <small>{formData.title.length}/300</small>
          </div>

          {/* Excerpt */}
          <div className="blog_form_group">
            <label htmlFor="excerpt">Resumo / Descrição</label>
            <textarea
              id="excerpt"
              name="excerpt"
              value={formData.excerpt}
              onChange={handleInputChange}
              placeholder="Resumo do seu artigo (será preenchido automaticamente se deixar em branco)"
              maxLength="500"
              rows="3"
              className="blog_textarea"
            />
            <small>{formData.excerpt.length}/500</small>
          </div>

          {/* Content */}
          <div className="blog_form_group">
            <label htmlFor="content">Conteúdo *</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              placeholder="Escreva o conteúdo completo do seu artigo (mínimo 100 caracteres)"
              minLength="100"
              maxLength="50000"
              rows="15"
              className="blog_textarea blog_content_area"
            />
            <small>{formData.content.length}/50000</small>
          </div>

          {/* Category */}
          <div className="blog_form_group">
            <label htmlFor="category">Categoria *</label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="blog_select"
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Tags */}
          <div className="blog_form_group">
            <label htmlFor="tags">Tags (separadas por vírgula)</label>
            <input
              type="text"
              id="tags"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
              placeholder="ex: tecnologia, software, desenvolvimiento"
              className="blog_input"
            />
            <small>Use vírgulas para separar as tags</small>
          </div>

          {/* Visibility */}
          <div className="blog_form_group">
            <label htmlFor="visibility">Visibilidade</label>
            <select
              id="visibility"
              name="visibility"
              value={formData.visibility}
              onChange={handleInputChange}
              className="blog_select"
            >
              <option value="public">Público</option>
              <option value="private">Privado</option>
              <option value="followers">Apenas Seguidores</option>
              <option value="friends">Apenas Amigos</option>
            </select>
          </div>

          {/* Buttons */}
          <div className="blog_form_buttons">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="blog_btn_secondary"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="blog_btn_primary"
            >
              {loading
                ? "Publicando..."
                : isEditing
                ? "Atualizar Artigo"
                : "Publicar Artigo"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
